import * as React from 'react';

import { normalize } from '@/utils';

import {
  Ball,
  BOUNCE_MAGNITUDE,
  BOUNCE_TIME,
} from './CssVsJs.helpers';

interface Props {
  isPlaying: boolean;
}

function BallRaf({ isPlaying }: Props) {
  const ref = React.useRef<HTMLDivElement | null>(null);

  useRafBasedBounce({
    isPlaying,
    ref,
  });

  return <Ball ref={ref} />;
}

function useRafBasedBounce({
  isPlaying,
  ref,
}: {
  isPlaying: boolean;
  ref: React.RefObject<HTMLDivElement | null>;
}) {
  const animationRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const startAt = performance.now();

    const animate = () => {
      const ball = ref.current;

      if (!isPlaying || !ball) {
        return;
      }

      const totalTimeElapsed = performance.now() - startAt;

      const bounceIndex = Math.floor(totalTimeElapsed / BOUNCE_TIME);
      const bounceDirection =
        bounceIndex % 2 === 0 ? 'going-right' : 'going-left';

      const bounceProgress = totalTimeElapsed % BOUNCE_TIME;

      const offset =
        bounceDirection === 'going-right'
          ? normalize(
              bounceProgress,
              0,
              BOUNCE_TIME,
              0,
              BOUNCE_MAGNITUDE
            )
          : normalize(
              bounceProgress,
              0,
              BOUNCE_TIME,
              BOUNCE_MAGNITUDE,
              0
            );

      ball.style.transform = `translateX(${offset}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animate);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, ref]);
}

export default BallRaf;
